/**
 * 认证 API 服务
 */

import type {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  TelegramAuthRequest,
  RefreshTokenRequest,
  UserInfo,
  SecurityFields,
  AuthStatusResponse,
} from '@/types/api'
import { post, saveTokens, clearTokens, getRefreshToken, isTokenExpired } from '@/services/request'

/**
 * 用户登录
 * @param username 用户名
 * @param password 密码
 */
export async function login(
  username: string,
  password: string
): Promise<{ success: boolean; data?: AuthResponse; error?: string; errorCode?: number }> {
  try {
    const response = await post<Omit<LoginRequest, keyof SecurityFields>, AuthResponse>('/api/v1/auth/login', {
      username,
      password,
    })

    if (response.code === 0 && response.data) {
      // 保存 Token (snake_case)
      saveTokens(response.data.access_token, response.data.refresh_token, response.data.expires_at)

      return { success: true, data: response.data }
    }

    return {
      success: false,
      error: response.msg || '登录失败',
      errorCode: response.code,
    }
  } catch (error) {
    console.error('Login error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 用户注册
 * @param username 用户名
 * @param password 密码
 * @param inviteCode 邀请码（可选）
 */
export async function register(
  username: string,
  password: string,
  inviteCode?: string
): Promise<{ success: boolean; data?: AuthResponse; error?: string; errorCode?: number }> {
  try {
    const requestData: Omit<RegisterRequest, keyof SecurityFields> = {
      username,
      password,
    }

    if (inviteCode) {
      requestData.invite_code = inviteCode
    }

    const response = await post<typeof requestData, AuthResponse>('/api/v1/auth/register', requestData)

    if (response.code === 0 && response.data) {
      // 保存 Token (snake_case)
      saveTokens(response.data.access_token, response.data.refresh_token, response.data.expires_at)

      return { success: true, data: response.data }
    }

    return {
      success: false,
      error: response.msg || '注册失败',
      errorCode: response.code,
    }
  } catch (error) {
    console.error('Register error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 刷新 Token
 */
export async function refreshToken(): Promise<{
  success: boolean
  data?: AuthResponse
  error?: string
}> {
  const token = getRefreshToken()
  if (!token) {
    return { success: false, error: '无刷新 Token' }
  }

  try {
    const response = await post<Omit<RefreshTokenRequest, keyof SecurityFields>, AuthResponse>('/api/v1/auth/refresh', {
      refresh_token: token,
    })

    if (response.code === 0 && response.data) {
      saveTokens(response.data.access_token, response.data.refresh_token, response.data.expires_at)
      return { success: true, data: response.data }
    }

    // Token 刷新失败，清除本地存储
    clearTokens()
    return { success: false, error: response.msg || '刷新失败' }
  } catch (error) {
    console.error('Refresh token error:', error)
    clearTokens()
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取用户信息
 */
export async function getUserInfo(): Promise<{
  success: boolean
  data?: UserInfo
  error?: string
}> {
  try {
    // 检查 Token 是否过期，如果过期先刷新
    if (isTokenExpired()) {
      const refreshResult = await refreshToken()
      if (!refreshResult.success) {
        return { success: false, error: '认证已过期，请重新登录' }
      }
    }

    const response = await post<object, UserInfo>('/api/v1/user/info', {}, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取用户信息失败' }
  } catch (error) {
    console.error('Get user info error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * Telegram Mini App 认证
 * @param initData Telegram initData 完整字符串
 * @param inviteCode 邀请码（可选）
 */
export async function loginWithTelegram(
  initData: string,
  inviteCode?: string
): Promise<{ success: boolean; data?: AuthResponse; error?: string; errorCode?: number }> {
  try {
    const requestData: Omit<TelegramAuthRequest, keyof SecurityFields> = { init_data: initData }
    if (inviteCode) {
      requestData.invite_code = inviteCode
    }

    const response = await post<typeof requestData, AuthResponse>('/api/v1/auth/telegram', requestData)

    if (response.code === 0 && response.data) {
      saveTokens(response.data.access_token, response.data.refresh_token, response.data.expires_at)
      return { success: true, data: response.data }
    }

    return {
      success: false,
      error: response.msg || 'Telegram认证失败',
      errorCode: response.code,
    }
  } catch (error) {
    console.error('Telegram auth error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 自动 Telegram 登录
 * 检测环境 -> 加载SDK -> 获取initData -> 登录
 */
export async function autoLoginWithTelegram(): Promise<{
  success: boolean
  data?: AuthResponse
  error?: string
  errorCode?: number
}> {
  const { isTelegramEnv, initTelegram, getTelegramInitData, getTelegramInviteCode } = await import('@/utils/telegram')

  // 1. 检测是否在 Telegram 环境
  if (!isTelegramEnv()) {
    return { success: false, error: '非 Telegram 环境' }
  }

  // 2. 初始化 SDK
  const tg = await initTelegram()
  if (!tg) {
    return { success: false, error: 'Telegram SDK 初始化失败' }
  }

  // 3. 获取 initData
  const initData = getTelegramInitData()
  if (!initData) {
    return { success: false, error: '无法获取 Telegram 认证数据' }
  }

  // 4. 获取邀请码 (从 start_param)
  const inviteCode = getTelegramInviteCode() || undefined

  // 5. 调用登录接口
  return loginWithTelegram(initData, inviteCode)
}

/**
 * Google OAuth 登录
 * @param idToken Google GIS 返回的 id_token (credential)
 * @param inviteCode 邀请码（可选）
 */
export async function loginWithGoogle(
  idToken: string,
  inviteCode?: string
): Promise<{ success: boolean; data?: AuthResponse; error?: string; errorCode?: number }> {
  try {
    const requestData: Record<string, string | boolean> = {
      auth_type: 'google',
      auth_key: idToken,
      auth_secret: '',
      is_register: false,
    }
    if (inviteCode) {
      requestData.invite_code = inviteCode
    }

    const response = await post<typeof requestData, AuthResponse>('/api/v1/auth/authenticate', requestData)

    if (response.code === 0 && response.data) {
      saveTokens(response.data.access_token, response.data.refresh_token, response.data.expires_at)
      return { success: true, data: response.data }
    }

    return {
      success: false,
      error: response.msg || 'Google 登录失败',
      errorCode: response.code,
    }
  } catch (error) {
    console.error('Google auth error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 退出登录
 */
export function logout(): void {
  clearTokens()
}

/**
 * 检查是否已登录（本地检查）
 */
export function isLoggedIn(): boolean {
  return !isTokenExpired()
}

/**
 * 统一身份状态查询
 * 前端启动时调用，判断当前身份：
 * - 携带有效 JWT → mode: "user"
 * - 携带有效 X-Guest-Token → mode: "guest"
 * - 都没有 → mode: "anonymous"
 */
export async function getAuthStatus(): Promise<{
  success: boolean
  data?: AuthStatusResponse
  error?: string
}> {
  try {
    const response = await post<object, AuthStatusResponse>('/api/v1/auth/status', {})

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取身份状态失败' }
  } catch (error) {
    console.error('Get auth status error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}
